import { useEffect, useState } from "react";
import type { HelpArticle, HelpCategory, HelpContent } from "../types";
import { helpRu } from "./ru";

export type HelpHintId = keyof typeof helpRu.hints;

/**
 * Тексты справочника по языкам: русский — в сборке (запасной и для подсказок до загрузки), остальные грузятся по требованию.
 * Новый язык — content/<lang>/ с той же структурой (проверяет e2e/help-i18n.spec.ts), затем добавить в LOADERS.
 */
const LOADERS: Record<string, () => Promise<HelpContent>> = {
  en: () => import("./en").then((m) => m.helpEn),
  ky: () => import("./ky").then((m) => m.helpKy),
  uz: () => import("./uz").then((m) => m.helpUz),
};
const loaded: Record<string, HelpContent> = { ru: helpRu };
const pending: Record<string, Promise<void>> = {};

/** Загрузка перевода (один раз на язык; при сбое — повтор при следующем вызове) */
function load(lang: string): Promise<void> {
  const loader = LOADERS[lang];
  if (!loader || loaded[lang]) return Promise.resolve();
  return (pending[lang] ??= loader()
    .then((c) => { loaded[lang] = c; })
    .catch(() => { delete pending[lang]; }));
}

/** Справочник на языке интерфейса; пока перевод грузится — русский без баннера, языка нет — русский с баннером. */
export function helpContent(lang: string): { content: HelpContent; translated: boolean } {
  const c = loaded[lang];
  return c ? { content: c, translated: true } : { content: helpRu, translated: lang in LOADERS };
}

/** То же в компоненте: грузит перевод и перерисовывает, когда он готов. */
export function useHelpContent(lang: string) {
  const [, setReady] = useState(0);
  useEffect(() => {
    if (loaded[lang] || !LOADERS[lang]) return;
    let alive = true;
    void load(lang).then(() => { if (alive) setReady((x) => x + 1); });
    return () => { alive = false; };
  }, [lang]);
  return helpContent(lang);
}

export function findArticle(content: HelpContent, id: string): { article: HelpArticle; category: HelpCategory } | null {
  for (const category of content.categories) {
    const article = category.articles.find((a) => a.id === id);
    if (article) return { article, category };
  }
  return null;
}

/** Раздел программы → статья справочника (кнопка «?» в шапке). */
export function articleForPath(content: HelpContent, path: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const c of content.categories) {
    for (const a of c.articles) {
      if (a.path && (path === a.path || path.startsWith(`${a.path}/`)) && (!best || a.path.length > best.len)) best = { id: a.id, len: a.path.length };
    }
  }
  // Разделы без своей статьи
  const extra: Record<string, string> = { "/kitchen-stations": "kds", "/supplier-returns": "suppliers", "/dashboard": "dashboard-reports", "/staff/roles": "users-roles", "/staff/positions": "employees-positions", "/settings/company": "company-branches", "/courier": "delivery", "/onboarding": "onboarding" };
  return extra[path] ?? best?.id ?? null;
}

/** Текст статьи для поиска (заголовок, описание, ключевые слова, блоки) в нижнем регистре. */
export function articleText(a: HelpArticle): string {
  const parts: string[] = [a.title, a.summary, ...(a.keywords ?? [])];
  for (const b of a.body) {
    if (b.t === "p" || b.t === "h" || b.t === "tip" || b.t === "warn") parts.push(b.text);
    else if (b.t === "steps" || b.t === "list") parts.push(b.title ?? "", ...b.items);
    else if (b.t === "terms") parts.push(b.title ?? "", ...b.items.flatMap((x) => [x.term, x.text]));
    else if (b.t === "faq") parts.push(...b.items.flatMap((x) => [x.q, x.a]));
    else if (b.t === "video") parts.push(b.title, "видео video");
  }
  return parts.join(" \n").toLowerCase();
}
